import { createContext, useContext, useReducer, useState } from 'react';

import { useLazyQuery, useMutation, useQuery } from '@apollo/client';

import {
	GET_ASSOCIATES_PAGINATE,
	GET_ASSOCIATE,
	CREATE_ASSOCIATE,
	UPDATE_ASSOCIATE,
	UPDATE_ASSOCIATE_STATUS,
	DELETE_ASSOCIATE
} from 'graphql/associates';

import { ACTION_TYPES, reducers } from './reducers';

const AssociatesStateContext = createContext();
const AssociatesDispatchContext = createContext();

const initialState = {
	rows: [],
	page: 1,
	perPage: 15,
	associate: {}
};

const AssociatesProvider = ({ children }) => {
	const [state, dispatch] = useReducer(reducers, initialState);
	const [toDelete, setToDelete] = useState(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	return (
		<AssociatesStateContext.Provider
			value={{
				...state,
				toDelete,
				setToDelete,
				deleteModalOpen,
				setDeleteModalOpen
			}}
		>
			<AssociatesDispatchContext.Provider value={dispatch}>
				{children}
			</AssociatesDispatchContext.Provider>
		</AssociatesStateContext.Provider>
	);
};

const useAssociates = () => {
	const context = useContext(AssociatesStateContext);

	if (!context)
		throw new Error(
			'useAssociates must be used within an AssociatesProvider'
		);

	return context;
};

const useDispatch = () => {
	const dispatch = useContext(AssociatesDispatchContext);

	if (dispatch === undefined)
		throw new Error('useDispatch must be used within a AssociatesProvider');

	return dispatch;
};

const usePaginateAssociates = () => {
	const dispatch = useDispatch();

	return useLazyQuery(GET_ASSOCIATES_PAGINATE, {
		onCompleted: (data) => {
			window.history.replaceState(
				null,
				'',
				`./associados?page=${data.paginateAssociates.paginatorInfo.currentPage}&size=${data.paginateAssociates.paginatorInfo.perPage}`
			);
			dispatch({
				type: ACTION_TYPES.LIST,
				data: data.paginateAssociates
			});
		}
	});
};

const useCreateAssociate = (options = {}) => {
	const dispatch = useDispatch();
	const { onSuccess, onError } = options;

	return useMutation(CREATE_ASSOCIATE, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess(data);
			dispatch({ type: ACTION_TYPES.ADD, data: data.createAssociate });
		},
		onError: (error) => {
			if (onError) onError(error);
		}
	});
};

const useAssociate = (id) => {
	const dispatch = useContext(AssociatesDispatchContext);

	return useQuery(GET_ASSOCIATE, {
		fetchPolicy: 'no-cache',
		variables: {
			id
		},
		onCompleted: (data) =>
			dispatch({ type: ACTION_TYPES.VIEW, data: data.getAssociate })
	});
};

const useUpdateAssociate = (options = {}) => {
	const dispatch = useContext(AssociatesDispatchContext);
	const { onSuccess, onError } = options;

	return useMutation(UPDATE_ASSOCIATE, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess(data);
			dispatch({ type: ACTION_TYPES.UPDATE, data: data.updateAssociate });
		},
		onError: (error) => {
			if (onError) onError(error);
		},
		refetchQueries: [
			{
				query: GET_ASSOCIATES_PAGINATE
			}
		]
	});
};

const useUpdateAssociateStatus = (options = {}) => {
	const dispatch = useContext(AssociatesDispatchContext);
	const { onSuccess, onError } = options;

	return useMutation(UPDATE_ASSOCIATE_STATUS, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess(data);
			dispatch({
				type: ACTION_TYPES.UPDATE_STATUS,
				data: data.updateAssociateStatus
			});
		},
		onError: (error) => {
			if (onError) onError(error);
		}
	});
};

const useDeleteAssociate = (options = {}) => {
	const dispatch = useDispatch();
	const { onSuccess } = options;

	return useMutation(DELETE_ASSOCIATE, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess();
			dispatch({
				type: ACTION_TYPES.REMOVE,
				data: data.deleteAssociate
			});
		}
	});
};

export {
	AssociatesProvider,
	useAssociates,
	useCreateAssociate,
	usePaginateAssociates,
	useAssociate,
	useUpdateAssociate,
	useUpdateAssociateStatus,
	useDeleteAssociate
};
